import React, { useEffect, useState } from "react";

/* ============================================================
                      RESULT SUMMARY MODAL
   ============================================================ */

function ResultSummaryModal({
  onClose,
  onReview,
  total,
  correct,
  wrong,
  score,
  timeUsedSec,
}) {
  const skipped = total - (correct + wrong);
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

  const minutes = String(Math.floor(timeUsedSec / 60)).padStart(2, "0");
  const seconds = String(timeUsedSec % 60).padStart(2, "0");

  return (
    <div style={styles.modalWrap}>
      <div style={styles.modalBox}>
        <h2>Exam Summary</h2>

        <p><strong>Total Questions:</strong> {total}</p>
        <p><strong>Correct:</strong> {correct}</p>
        <p><strong>Wrong:</strong> {wrong}</p>
        <p><strong>Skipped:</strong> {skipped}</p>
        <p><strong>Score:</strong> {score.toFixed(2)}</p>
        <p><strong>Accuracy:</strong> {accuracy}%</p>
        <p><strong>Time Used:</strong> {minutes}:{seconds}</p>

        <button style={styles.btnPrimary} onClick={onReview}>
          Review Answers
        </button>

        <button style={styles.btnSecondary} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/* ============================================================
                      REVIEW MODE COMPONENT
   ============================================================ */

function ReviewMode({ answers, onClose }) {
  return (
    <div style={styles.modalWrap}>
      <div
        style={{
          ...styles.modalBox,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h2>Review Answers</h2>

        {answers.map((q, i) => {
          const isCorrect = q.selected === q.correct;

          return (
            <div
              key={i}
              style={{
                marginBottom: "18px",
                padding: "12px",
                borderRadius: "8px",
                background: isCorrect
                  ? "#d4fcdc"
                  : q.selected
                  ? "#ffe0e0"
                  : "#f3f4f6",
                border: `1px solid ${
                  isCorrect ? "#10b981" : q.selected ? "#ef4444" : "#ccc"
                }`,
              }}
            >
              <p><strong>{i + 1}. {q.question}</strong></p>
              <p>Your Answer: <strong>{q.selected || "Skipped"}</strong></p>
              <p>Correct Answer: <strong>{q.correct}</strong></p>
            </div>
          );
        })}

        <button style={styles.btnPrimary} onClick={onClose}>
          Close Review
        </button>
      </div>
    </div>
  );
}

/* ===================== MAIN QUIZ COMPONENT ===================== */

export default function QuizGoogleSheet({
  sheetName,
  timeLeft,
  totalDuration,
  onMetaChange,
  onScoreChange,
  onProgressChange,
}) {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const [mcqMode, setMcqMode] = useState("all");
  const [shuffle, setShuffle] = useState(false);

  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);

  const [answers, setAnswers] = useState([]);

  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const NEGATIVE = 0.25;

  const sheetId = "1tS23XjQpdM_Xz7dDd8YT5hgpVJ09mbq5zSX2azymcWM";
  const url = `https://opensheet.elk.sh/${sheetId}/${sheetName}`;

  /* ------------ Load Questions ------------ */

  useEffect(() => {
    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setQuestions(data);
        applyFilters(data, mcqMode, shuffle);
        setLoading(false);
      });
  }, [sheetName]);

  /* ------------ Apply MCQ Mode + Shuffle ------------ */

  const applyFilters = (allQ, mode, shuffleEnabled) => {
    let q = [...allQ];

    if (mode === "50") q = q.slice(0, 50);
    if (mode === "100") q = q.slice(0, 100);

    if (shuffleEnabled) {
      q = q
        .map((item) => ({ item, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ item }) => item);
    }

    setFilteredQuestions(q);
    setCurrent(0);
    setSelected(null);
    setAnswers([]);

    setCorrectCount(0);
    setWrongCount(0);
    setScore(0);

    onMetaChange?.({ total: q.length });
    onScoreChange?.(0);
    onProgressChange?.({ attemptedCount: 0 });
  };

  /* ------------ Auto-submit from timer ------------ */
  useEffect(() => {
    const handler = () => setShowResult(true);
    window.addEventListener("autoSubmitExam", handler);
    return () => window.removeEventListener("autoSubmitExam", handler);
  }, []);

  if (loading) return <p>Loading...</p>;
  if (!filteredQuestions.length) return <p>No questions available.</p>;

  const totalQuestions = filteredQuestions.length;
  const q = filteredQuestions[current];

  const timeUsedSec = totalDuration - timeLeft;

  const options = [
    { label: "A", text: q.optionA },
    { label: "B", text: q.optionB },
    { label: "C", text: q.optionC },
    { label: "D", text: q.optionD },
  ];

  /* ------------ Answer Selection ------------ */

  const handleSelect = (ans) => {
    if (selected) return;

    setSelected(ans);

    const isCorrect = ans === q.correct;

    const newAnswers = [...answers];
    newAnswers[current] = {
      question: q.question,
      correct: q.correct,
      selected: ans,
      explanation: q.explanation || "",
    };
    setAnswers(newAnswers);

    if (isCorrect) {
      setCorrectCount((p) => p + 1);
      setScore((p) => p + 1);
      onScoreChange?.(score + 1);
    } else {
      setWrongCount((p) => p + 1);
      setScore((p) => p - NEGATIVE);
      onScoreChange?.(score - NEGATIVE);
    }

    onProgressChange?.({
      attemptedCount: correctCount + wrongCount + 1,
    });
  };

  /* ------------ Next Question ------------ */

  const handleNext = () => {
    if (current === totalQuestions - 1) {
      setShowResult(true);
      return;
    }

    setCurrent((p) => p + 1);
    setSelected(null);
  };

  /* ===================== RENDER ===================== */

  return (
    <div style={styles.box}>
      <h2>{sheetName.toUpperCase()} Quiz</h2>

      {/* MCQ MODE */}
      <div style={styles.row}>
        <label>Select Mode:</label>
        <select
          value={mcqMode}
          onChange={(e) => {
            setMcqMode(e.target.value);
            applyFilters(questions, e.target.value, shuffle);
          }}
          style={styles.select}
        >
          <option value="all">All Questions</option>
          <option value="50">MCQ-50</option>
          <option value="100">MCQ-100</option>
        </select>
      </div>

      {/* SHUFFLE */}
      <div style={styles.row}>
        <label>Shuffle:</label>
        <input
          type="checkbox"
          checked={shuffle}
          onChange={(e) => {
            setShuffle(e.target.checked);
            applyFilters(questions, mcqMode, e.target.checked);
          }}
        />
      </div>

      {/* QUESTION */}
      <h3>{current + 1}. {q.question}</h3>

      {options.map((op) => (
        <button
          key={op.label}
          onClick={() => handleSelect(op.label)}
          style={{
            ...styles.option,
            ...(selected &&
              (op.label === q.correct
                ? styles.correct
                : selected === op.label
                ? styles.wrong
                : {})),
          }}
        >
          {op.label}. {op.text}
        </button>
      ))}

      {/* ============================
           EXPLANATION ADDED
      ============================ */}
      {selected && (
        <div
          style={{
            background: "#f3f4f6",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ddd",
            marginTop: "15px",
          }}
        >
          <h5 style={{ marginBottom: "6px" }}>📘 ব্যাখ্যা (Explanation):</h5>
          <p style={{ margin: 0 }}>
            {q.explanation && q.explanation.trim() !== ""
              ? q.explanation
              : "No explanation provided."}
          </p>
        </div>
      )}

      {/* NEXT BUTTON */}
      <button
        onClick={handleNext}
        disabled={!selected}
        style={{
          ...styles.next,
          background: selected ? "#2563eb" : "#aaa",
        }}
      >
        {current === totalQuestions - 1 ? "Finish" : "Next"}
      </button>

      {/* FINISH ANYTIME BUTTON */}
      <button
        onClick={() => setShowResult(true)}
        style={styles.finishAny}
      >
        Finish Exam Now
      </button>

      {/* RESULT MODAL */}
      {showResult && (
        <ResultSummaryModal
          onClose={() => setShowResult(false)}
          onReview={() => {
            setShowReview(true);
            setShowResult(false);
          }}
          total={totalQuestions}
          correct={correctCount}
          wrong={wrongCount}
          score={score}
          timeUsedSec={timeUsedSec}
        />
      )}

      {/* REVIEW MODE */}
      {showReview && (
        <ReviewMode
          answers={answers}
          onClose={() => setShowReview(false)}
        />
      )}
    </div>
  );
}

/* ===================== STYLES ===================== */

const styles = {
  box: {
    background: "#fff",
    padding: 20,
    borderRadius: 10,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },
  row: {
    marginBottom: 10,
    fontWeight: 600,
  },
  select: {
    padding: "6px 10px",
    marginLeft: 10,
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  option: {
    width: "100%",
    padding: 12,
    marginBottom: 10,
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 16,
    textAlign: "left",
    cursor: "pointer",
  },
  correct: {
    background: "#d4fcdc",
    borderColor: "#10b981",
  },
  wrong: {
    background: "#ffe0e0",
    borderColor: "#ef4444",
  },
  next: {
    width: "100%",
    padding: 12,
    color: "#fff",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    marginTop: 10,
  },
  finishAny: {
    width: "100%",
    padding: 12,
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "600",
    marginTop: 10,
  },
  modalWrap: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  modalBox: {
    background: "#fff",
    padding: 20,
    borderRadius: 10,
    width: "95%",
    maxWidth: 500,
  },
  btnPrimary: {
    width: "100%",
    padding: 10,
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    marginTop: 10,
  },
  btnSecondary: {
    width: "100%",
    padding: 10,
    background: "#777",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    marginTop: 10,
  },
};
